/**
 * LipaFlow App Theme Constants
 *
 * Centralized branding and styling references for the institution portal.
 * All components and pages should import from this file instead of
 * hardcoding colors, status mappings, or formatting functions.
 *
 * Brand Principles:
 * 1. PROGRESS OVER PROBLEMS - Lead with "67% collected" not "33% outstanding"
 * 2. INFORMATION, NOT ALARM - Warm orange for "action needed", never red for money
 * 3. HUMANS ARE FAMILIES - Display "Kamau Family" not "Client #4729"
 * 4. GUIDE, DON'T OVERWHELM - Surface the most important action
 * 5. CELEBRATE WINS - Show success animations, highlight progress
 */

import { useAuthStore } from '@lipaflow/shared'

// --- Badge Variant Type (shared across components) ---
export type BadgeVariant = 'teal' | 'clay' | 'mint' | 'info' | 'warning' | 'success' | 'neutral'

// --- Status → Badge Variant Mappings ---

export const claimStatusVariant: Record<string, BadgeVariant> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'clay',
  expired: 'neutral',
}

export const claimStatusLabel: Record<string, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  expired: 'Expired',
}

export const sessionStateVariant: Record<string, BadgeVariant> = {
  invited: 'info',
  engaged: 'success',
  negotiating: 'warning',
  committed: 'teal',
  claimed: 'clay',
  verified: 'success',
  disputed: 'clay',
  dormant: 'neutral',
  escalated: 'warning',
  opted_out: 'neutral',
  closed: 'neutral',
}

export const sessionStateLabel: Record<string, string> = {
  invited: 'Invited',
  engaged: 'Engaged',
  negotiating: 'Negotiating',
  committed: 'Committed',
  claimed: 'Claimed',
  verified: 'Verified',
  disputed: 'Disputed',
  dormant: 'Dormant',
  escalated: 'Needs Attention',
  opted_out: 'Opted Out',
  closed: 'Closed',
}

export const ticketPriorityVariant: Record<string, BadgeVariant> = {
  urgent: 'clay',
  high: 'warning',
  medium: 'info',
  low: 'success',
}

export const ticketStatusVariant: Record<string, BadgeVariant> = {
  open: 'info',
  assigned: 'teal',
  in_progress: 'warning',
  pending_customer: 'neutral',
  resolved: 'success',
  closed: 'neutral',
}

export const ticketStatusLabel: Record<string, string> = {
  open: 'Open',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  pending_customer: 'Pending',
  resolved: 'Resolved',
  closed: 'Closed',
}

export const obligationStatusVariant: Record<string, BadgeVariant> = {
  pending: 'info',
  partial: 'warning',
  paid: 'success',
  overdue: 'clay', // NOT red — brand principle #2
  waived: 'neutral',
}

export const pledgeStatusVariant: Record<string, BadgeVariant> = {
  promised: 'info',
  partial: 'warning',
  fulfilled: 'success',
  missed: 'clay',
  rolled_over: 'neutral',
  cancelled: 'neutral',
}

export const broadcastStatusVariant: Record<string, BadgeVariant> = {
  draft: 'neutral',
  scheduled: 'info',
  pending_approval: 'warning',
  approved: 'teal',
  in_progress: 'teal',
  completed: 'success',
  cancelled: 'neutral',
  failed: 'clay',
}

// --- Currency Formatting ---

const currencyFormatterCache = new Map<string, Intl.NumberFormat>()

function resolveCurrency(): string {
  const tenantCurrency = useAuthStore.getState().tenant?.settings?.currency
  return tenantCurrency?.trim() || 'KES'
}

function getCurrencyFormatter(currency: string, detailed: boolean): Intl.NumberFormat {
  const locale = currency === 'KES' ? 'en-KE' : 'en-US'
  const key = `${locale}:${currency}:${detailed ? 'detailed' : 'compact'}`
  const existing = currencyFormatterCache.get(key)
  if (existing) return existing

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: detailed ? 2 : 0,
    maximumFractionDigits: detailed ? 2 : 0,
  })
  currencyFormatterCache.set(key, formatter)
  return formatter
}

export function formatCurrency(amount: number, detailed = false, currency?: string): string {
  const resolvedCurrency = (currency || resolveCurrency()).toUpperCase()
  const formatter = getCurrencyFormatter(resolvedCurrency, detailed)
  return formatter.format(amount)
}

// --- Date/Time Formatting ---

export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleDateString('en-KE', { dateStyle: 'medium' })
}

export function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-KE', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

export function formatTime(dateString: string): string {
  return new Date(dateString).toLocaleTimeString('en-KE', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function timeAgo(dateString: string): string {
  const diff = Date.now() - new Date(dateString).getTime()
  const days = Math.floor(diff / 86400000)
  const hours = Math.floor(diff / 3600000)
  const minutes = Math.floor(diff / 60000)
  if (days > 0) return `${days}d ago`
  if (hours > 0) return `${hours}h ago`
  if (minutes > 0) return `${minutes}m ago`
  return 'Just now'
}

export function daysUntil(dateString: string): number {
  return Math.ceil((new Date(dateString).getTime() - Date.now()) / 86400000)
}

export function formatTimeRemaining(hours: number): string {
  if (hours <= 0) return 'Overdue'
  if (hours < 1) return `${Math.round(hours * 60)}m`
  if (hours < 24) return `${hours.toFixed(1)}h`
  return `${Math.round(hours / 24)}d`
}

// --- Text Helpers ---

export function pluralize(s: string): string {
  if (s.endsWith('y')) return s.slice(0, -1) + 'ies'
  if (s.endsWith('s') || s.endsWith('x') || s.endsWith('ch')) return s + 'es'
  return s + 's'
}

export function greeting(): string {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}
